// Tiny typed fetch helper. Surfaces API errors as Error instances so the UI
// can show them directly. In dev the base URL is empty so `/api/v1/...` is
// proxied by Vite to the local Worker. In prod `VITE_API_URL` must point at
// the deployed Worker (e.g. https://thankd-api.<account>.workers.dev).

const API_BASE = (import.meta.env.VITE_API_URL ?? "").replace(/\/$/, "");

export interface ApiErrorShape {
  error: { code: string; message: string; details?: unknown };
}

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
    public details?: unknown,
  ) {
    super(message);
  }
}

const request = async <T>(
  method: string,
  path: string,
  body?: unknown,
): Promise<T> => {
  const res = await fetch(`${API_BASE}/api/v1${path}`, {
    method,
    credentials: "include",
    headers: body ? { "content-type": "application/json" } : undefined,
    body: body ? JSON.stringify(body) : undefined,
  });
  const contentType = res.headers.get("content-type") ?? "";
  const looksJson = contentType.includes("application/json");
  if (!res.ok) {
    let code = "error";
    let message = res.statusText || `HTTP ${res.status}`;
    let details: unknown;
    if (looksJson) {
      try {
        const j = (await res.json()) as ApiErrorShape;
        code = j.error?.code ?? code;
        message = j.error?.message ?? message;
        details = j.error?.details;
      } catch {
        // ignore JSON parse failures
      }
    }
    throw new ApiError(res.status, code, message, details);
  }
  if (res.status === 204) return undefined as T;
  if (!looksJson) {
    // A 200 that isn't JSON usually means the request was served by the SPA
    // host instead of the API — most often a missing VITE_API_URL in prod.
    throw new ApiError(
      res.status,
      "bad_response",
      "API returned a non-JSON response. Check that VITE_API_URL points at the API Worker.",
    );
  }
  return (await res.json()) as T;
};

export const api = {
  get: <T>(path: string) => request<T>("GET", path),
  post: <T>(path: string, body?: unknown) => request<T>("POST", path, body),
  put: <T>(path: string, body?: unknown) => request<T>("PUT", path, body),
  patch: <T>(path: string, body?: unknown) => request<T>("PATCH", path, body),
  delete: <T>(path: string) => request<T>("DELETE", path),
};
